import React from 'react';
import {
  Box,
  Collapse,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ComponentEditor from '../ComponentEditor';

/**
 * The blocks in a section, as the tree they actually are.
 *
 * Two things were missing and they were the same thing: there was no way to
 * move a block, and no way to put one inside another. So a pricing section
 * was three headings in a fixed order that the section type happened to lay
 * out in a row — change your mind about which plan comes first and the only
 * option was retyping both.
 *
 * Every row carries its own up, down, add-inside and delete. Up and down move
 * a block among its siblings, never out of its parent: a card's second item
 * moving "up" past the card entirely is never what the arrow looked like it
 * would do.
 *
 * Rows collapse. Twelve blocks with every editor open is the wall of
 * dropdowns this editor replaced, so only the one being worked on is open.
 */

const summarise = (node) => {
  if (node.name && node.name !== `${node.type} component`) return node.name;
  const text = node.content?.text || node.content?.alt || node.content?.caption;
  if (text) return String(text).slice(0, 38);
  if (node.type === 'grid') return `Grid of ${node.children?.length || 0}`;
  if (node.type === 'card') return 'Card';
  if (node.type === 'image') return 'Picture';
  return node.type;
};

export default function BlockTree({
  nodes, depth = 0, openId, onToggleOpen,
  onMove, onDropBlock, onAddInside, onDelete, onPatch,
  catalog, brandId, containerTypes,
  drag, setDrag,
}) {
  if (!nodes?.length) return null;

  return (
    <Stack spacing='6px'>
      {nodes.map((node, i) => {
        const isOpen = openId === node._id;
        const isContainer = containerTypes.has(node.type);
        const isDragging = drag?.id === node._id;
        const isDropTarget = drag?.over === node._id && drag?.id !== node._id;
        return (
          <Box key={node._id}>
            <Box
              onDragOver={(e) => {
                if (!drag?.id) return;
                // Siblings only — the same rule sections follow. Signalling a
                // drop that would then be refused is worse than refusing to
                // signal it.
                if (!nodes.some((n) => n._id === drag.id)) return;
                e.preventDefault();
                e.stopPropagation();
                setDrag((d) => ({ ...d, over: node._id }));
              }}
              onDrop={(e) => {
                if (!drag?.id) return;
                e.preventDefault();
                e.stopPropagation();
                onDropBlock(drag.id, node._id);
                setDrag(null);
              }}
              sx={{
                border: '1px solid',
                borderColor: isDropTarget ? 'primary.main' : (isOpen ? 'primary.main' : 'divider'),
                boxShadow: isDropTarget ? (t) => `0 0 0 2px ${t.palette.primary.main}` : 'none',
                opacity: isDragging ? 0.45 : 1,
                borderRadius: '10px',
                bgcolor: 'background.paper',
                // Indent shows nesting without a second column of guides,
                // which at 360px wide would cost more than it explains.
                ml: depth ? `${Math.min(depth, 3) * 12}px` : 0,
              }}
            >
              <Stack
                direction='row'
                alignItems='center'
                gap='2px'
                sx={{ p: '4px 4px 4px 6px' }}
              >
                {/* Drag from the grip, not from the row: the rest of the row
                    is a button that opens the block, and a row that both
                    opens on click and moves on drag makes every click feel
                    like a gamble. */}
                <Box
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', node._id);
                    setDrag({ id: node._id, over: null });
                  }}
                  onDragEnd={() => setDrag(null)}
                  aria-label={`Drag ${summarise(node)}`}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    cursor: 'grab', color: 'text.disabled',
                    '&:active': { cursor: 'grabbing' },
                    '&:hover': { color: 'text.secondary' },
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 16 }} />
                </Box>

                <Box
                  component='button'
                  type='button'
                  onClick={() => onToggleOpen(isOpen ? null : node._id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    flex: 1, minWidth: 0, textAlign: 'left',
                    border: 'none', background: 'none', font: 'inherit',
                    color: 'text.primary', cursor: 'pointer', p: '4px',
                  }}
                >
                  {isOpen
                    ? <ExpandMoreIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                    : <ChevronRightIcon fontSize='small' sx={{ color: 'text.secondary' }} />}
                  <Typography variant='body2' fontWeight='600' noWrap sx={{ minWidth: 0 }}>
                    {summarise(node)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
                    {node.type}
                  </Typography>
                </Box>

                <RowBtn
                  label='Move up'
                  disabled={i === 0}
                  onClick={() => onMove(node, -1)}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                </RowBtn>
                <RowBtn
                  label='Move down'
                  disabled={i === nodes.length - 1}
                  onClick={() => onMove(node, 1)}
                >
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                </RowBtn>
                {isContainer && (
                  <RowBtn label='Add inside this' onClick={() => onAddInside(node)}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </RowBtn>
                )}
                <RowBtn label='Delete' danger onClick={() => onDelete(node)}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </RowBtn>
              </Stack>

              <Collapse in={isOpen} unmountOnExit>
                <Box sx={{ p: '10px', borderTop: '1px solid', borderColor: 'divider' }}>
                  <ComponentEditor
                    bare
                    component={node}
                    catalog={catalog}
                    brandId={brandId}
                    onPatch={(patch) => onPatch(node, patch)}
                    onDelete={() => onDelete(node)}
                  />
                </Box>
              </Collapse>
            </Box>

            {node.children?.length > 0 && (
              <Box sx={{ mt: '6px' }}>
                <BlockTree
                  nodes={node.children}
                  depth={depth + 1}
                  openId={openId}
                  onToggleOpen={onToggleOpen}
                  onMove={onMove}
                  onDropBlock={onDropBlock}
                  drag={drag}
                  setDrag={setDrag}
                  onAddInside={onAddInside}
                  onDelete={onDelete}
                  onPatch={onPatch}
                  catalog={catalog}
                  brandId={brandId}
                  containerTypes={containerTypes}
                />
              </Box>
            )}

            {isContainer && !node.children?.length && (
              <Typography
                variant='caption'
                color='text.secondary'
                fontStyle='italic'
                sx={{ display: 'block', ml: `${Math.min(depth + 1, 3) * 12 + 10}px`, mt: '4px' }}
              >
                Empty — use + to put something in it.
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function RowBtn({ label, children, onClick, disabled, danger }) {
  return (
    <Tooltip title={label}>
      <span>
        <Box
          component='button'
          type='button'
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          sx={{
            border: 'none', background: 'none',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.3 : 1,
            color: danger ? 'error.main' : 'text.secondary',
            display: 'flex', alignItems: 'center',
            p: '5px', borderRadius: '6px', flexShrink: 0,
            '&:hover': { bgcolor: disabled ? 'transparent' : 'action.hover' },
          }}
        >
          {children}
        </Box>
      </span>
    </Tooltip>
  );
}
